import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";

export default function Login() {
  const [sp] = useSearchParams();
  const next = sp.get("next");
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [adminCode, setAdminCode] = useState("");
  const [showAdminField, setShowAdminField] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login, register } = useApp();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      let user;
      if (mode === "signin") {
        user = await login({ email, password });
      } else {
        if (password.length < 8) throw new Error("Password must be at least 8 characters.");
        user = await register({ email, password, name, admin_invite_code: adminCode || undefined });
      }
      toast(`Welcome${user.name ? `, ${user.name.split(" ")[0]}` : ""}`);
      navigate(next && next.startsWith("/") ? next : (user.role === "ADMINISTRATOR" ? "/admin" : "/my-temple"));
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Something went wrong.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid lg:grid-cols-2 relative overflow-hidden">
      <div className="hidden lg:block relative" style={{ background: "var(--surface-deep)" }}>
        <img
          src="https://images.unsplash.com/photo-1565884280295-98eb83e41c65?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(40,92,91,0.55), rgba(48,43,41,0.85))" }} />
        <div className="grain absolute inset-0" />
        <div className="relative h-full flex flex-col justify-end p-12" style={{ color: "var(--bg-cream)" }}>
          <div className="eyebrow mb-3" style={{ color: "var(--gold-soft)" }}>The threshold</div>
          <h2 className="font-serif text-5xl leading-tight">The door is already open.</h2>
          <p className="mt-6 max-w-md text-lg opacity-90">
            Sign in to your interior chamber. Continue a course, revisit a saved teaching, or simply sit for a moment
            in the company of the Temple.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 relative">
        <div className="bloom absolute inset-0" />
        <div className="w-full max-w-md relative">
          <div className="flex items-center gap-2 mb-8">
            <Sparkles className="w-5 h-5" style={{ color: "var(--gold-dark)" }} />
            <span className="eyebrow">Enter · My Temple</span>
          </div>
          <h1 className="font-serif text-4xl mb-2" style={{ color: "var(--burgundy)" }}>
            {mode === "signin" ? "Welcome back." : "Begin your journey."}
          </h1>
          <p className="text-sm mb-8" style={{ color: "var(--text-muted)" }}>
            {mode === "signin"
              ? "Sign in to My Temple with the email and password you created."
              : "Create your account. Your private reflections and progress stay with you."}
          </p>

          <div className="inline-flex p-1 rounded-full border mb-6" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
            {["signin", "signup"].map((m) => (
              <button
                key={m}
                data-testid={`auth-tab-${m}-btn`}
                type="button"
                onClick={() => setMode(m)}
                className="px-5 py-1.5 rounded-full text-xs font-medium"
                style={{ background: mode === m ? "var(--burgundy)" : "transparent", color: mode === m ? "var(--bg-cream)" : "var(--text-obsidian)" }}
              >
                {m === "signin" ? "Sign in" : "Sign up"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <input
                data-testid="signup-name-input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full rounded-lg border px-4 py-3 text-sm bg-transparent"
                style={{ borderColor: "var(--border-cream)" }}
              />
            )}
            <input
              data-testid="auth-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full rounded-lg border px-4 py-3 text-sm bg-transparent"
              style={{ borderColor: "var(--border-cream)" }}
            />
            <input
              data-testid="auth-password-input"
              type="password"
              required
              minLength={mode === "signup" ? 8 : undefined}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === "signup" ? "Create a password (min 8 characters)" : "Password"}
              className="w-full rounded-lg border px-4 py-3 text-sm bg-transparent"
              style={{ borderColor: "var(--border-cream)" }}
            />
            {mode === "signup" && showAdminField && (
              <input
                data-testid="admin-invite-input"
                value={adminCode}
                onChange={(e) => setAdminCode(e.target.value)}
                placeholder="Administrator invite code (optional)"
                className="w-full rounded-lg border px-4 py-3 text-sm bg-transparent font-mono"
                style={{ borderColor: "var(--gold-soft)" }}
              />
            )}
            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full rounded-full py-3 text-sm font-medium disabled:opacity-60"
              style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}
            >
              {submitting ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
            </button>
          </form>

          {mode === "signup" && (
            <button
              data-testid="toggle-admin-invite-btn"
              type="button"
              onClick={() => setShowAdminField((s) => !s)}
              className="mt-3 text-xs ink-link"
              style={{ color: "var(--text-muted)" }}
            >
              {showAdminField ? "Hide admin invite code" : "I have an administrator invite code"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
